<template>
    <!--
        IMPORTANT:

        before using this component:
            - web apis must be checked, so they work before this
            - install lodash
    -->
    <div class="postcode-lookup">

        <div v-if="!dropdownOpen" class="postcode-lookup__intro">
            <div class="form-group">
                <label class="step__form--question" for="lookupAddress">
                    Billing address*
                    <span v-if="searching">
                        Searching...
                    </span>
                </label>
                <div class="row postcode-lookup__container">
                    <div class="col-xs-12 col-sm-8" :class="{ 'form-group--error': $v.value.lookupAddress.$error }">
                        <input type="text"
                               class="form-control"
                               id="lookupAddress"
                               :value="value.lookupAddress"
                               @input="updateValue('lookupAddress', $event.target.value)"
                               @keydown="debouncedToggleFindAddress" />

                        <div class="error" v-if="!$v.value.lookupAddress.required">Please specify this information</div>
                        <transition name="fade">
                            <ul v-if="clusters.length" class="postcode-lookup__clusters">
                                <li v-for="cluster in clusters" :key="cluster.id">
                                    <button v-if="cluster.type === 'Address'" @click.prevent="toggleRetrieveAddress(cluster.id)" class="postcode-lookup__individual">
                                        {{ cluster.text }}
                                    </button>
                                    <button v-else @click.prevent="toggleFindAddress(cluster.id)" class="postcode-lookup__group">
                                        {{ cluster.text }}
                                    </button>
                                </li>
                            </ul>
                        </transition>
                    </div>
                    <div class="col-xs-12 col-sm-4 postcode-lookup__link-container">
                        <a href="#" @click.prevent="toggleDropdown" v-if="!dropdownOpen" class="postcode-lookup__link"><strong>Add address manually</strong></a>
                    </div>
 
                </div>
            </div>
        </div>

        <div class="postcode-lookup__dropdown" v-if="dropdownOpen">
            <div class="form-group" :class="{ 'form-group--error': $v.value.addressLine1.$error }">
                <label for="address1" class="step__form--question">Billing address - line 1*</label>
                <input type="text"
                       class="form-control"
                       id="address1"
                       :value="value.addressLine1"
                       @input="updateValue('addressLine1', $event.target.value)" />
                <div class="error" v-if="!$v.value.addressLine1.required">Please specify this information</div>

            </div>

            <div class="form-group">
                <label for="address2" class=" step__form--question">Billing address - line 2</label>
                <input type="text"
                       class="form-control"
                       id="address2"
                       :value="value.addressLine2"
                       @input="updateValue('addressLine2', $event.target.value)" />
            </div>

            <div class="form-group">
                <label for="address3" class="step__form--question">Billing address - line 3</label>
                <input type="text"
                       class="form-control"
                       id="address3"
                       :value="value.addressLine3"
                       @input="updateValue('addressLine3', $event.target.value)" />

            </div>

            <div class="form-group" :class="{ 'form-group--error': $v.value.city.$error }">
                <label for="city" class="step__form--question">City*</label>
                <input type="text"
                       class="form-control"
                       id="city"
                       :value="value.city"
                       @input="updateValue('city', $event.target.value)" />
                <div class="error" v-if="!$v.value.city.required">Please specify this information</div>

            </div>

            <div class="form-group">
                <label for="province" class="step__form--question">County</label>
                <input type="text"
                       class="form-control"
                       id="province"
                       :value="value.province"
                       @input="updateValue('province', $event.target.value)" />
            </div>

            <div class="form-group" :class="{ 'form-group--error': $v.value.postcode.$error }">
                <label for="postcode" class="step__form--question">Post code*</label>
                <input type="text"
                       class="form-control"
                       id="postcode"
                       :value="value.postcode"
                       @input="updateValue('postcode', $event.target.value)" />
                <div class="error" v-if="!$v.value.postcode.required">Please specify this information</div>

            </div>

        </div>
    </div>
</template>

<script>
    import axios from 'axios';
    import { required } from 'vuelidate/lib/validators';
    import _ from 'lodash';

    export default {
        props: {
            manual: {
                type: Boolean,
                default: false,
                required: false,
            },

            value: {
                type: Object,
                required: true
            },


        },

        data() {
            return {
                dropdownOpen: this.manual,
                searching: false,
                clusterId: null,
                clusters: [],
                countries: [],
                address: {
                    addressLine1: null,
                    addressLine2: null,
                    addressLine3: null,
                    city: null,
                    postcode: null,
                    country: null,
                    lookupAddress: null,
                }
            }
        },

        methods: {
            /**
             * Opens the full address dropdown
             * @param event - event object
             */
            toggleDropdown(event) {
                event.preventDefault();
                this.dropdownOpen = true;

            },

            /**
             * Updates v-model value via custom event emiting.
             * @param key - object property
             * @param val - property value
             */
            updateValue(key, val) {
                this.$emit('input', { ...this.value, [key]: val });
                if (this.$v.value.hasOwnProperty(key)) {
                    this.$v.value[key].$model = val;
                }
            },

            /**
             * Finds address based on the lookup address value;
             * */
            async findAddress(id) {
                const response = await axios.get('/quotetoolapi/contact/getaddresses', {
                    params: {
                        value: this.value.lookupAddress,
                        container: id
                    }
                })

                return response.data;
            },

            /**
             * Initializes find address functionality if there are more than 3 characters present in the address lookup field
             * */
            toggleFindAddress(id) {
                const { lookupAddress } = this.value;

                if (lookupAddress.length > 3 && !this.searching) {
                    this.searching = true;
                    this.findAddress(id).then(addresses => {
                        this.clusters = addresses;
                        this.searching = false;
                    });
                }
            },

            /**
             * Retrieve address
             * @param id
             */
            async retrieveAddress(id) {
                const response = await axios.get('/quotetoolapi/contact/getaddress', {
                    params: {
                        id
                    }
                })

                return response.data;
            },

            /**
             * Toggles retrieve address functionality and updates data
             * @param id - address ID
             */
            toggleRetrieveAddress(id) {
                this.retrieveAddress(id).then(address => {
                    const {
                        line1,
                        line2,
                        line3,
                        city,
                        postalCode,
                        province
                    } = address[0];

                    this.dropdownOpen = true;

                    this.updateValue('addressLine1', line1);
                    this.updateValue('addressLine2', line2);
                    this.updateValue('addressLine3', line3);
                    this.updateValue('city', city);
                    this.updateValue('province', province);
                    this.updateValue('postcode', postalCode);

                })
            }
        },

        created() {
            this.debouncedToggleFindAddress = _.debounce(this.toggleFindAddress, 500);
        },

        validations() {
            if (this.dropdownOpen) {
                return {
                    value: {
                        addressLine1: {
                            required,
                        },

                        city: {
                            required,
                        },

                        postcode: {
                            required,
                        },
                    }
                }
            } else {
                return {
                    value: {
                        lookupAddress: {
                            required,
                        }
                    }
                }
            }

        }
    }
</script>